import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCurrentUserRoles, isPlatformAdminUser } from '@/utils/userRole';
import { isRouteAllowed } from '@/constant/rolePermissions';
import { useSelector } from 'react-redux';
import { selectUserPermissions } from '@/store/authSlice';

interface Props {
  path: string;
  children?: React.ReactNode;
}

export function PermissionRoute({ path, children }: Props) {
  const location = useLocation();
  const roles = useCurrentUserRoles();
  const userPermissions = useSelector(selectUserPermissions);

  // Public routes without permission checks
  if (
    path === '/terms-and-conditions' ||
    path === '/privacy-policy' ||
    path === '/accessibility' ||
    path === '/faq' ||
    path === '/licensing' ||
    path === '/cookie-consent-settings'
  ) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/netvisor/accounting-exports' ||
    path === '/netvisor/accounting-exports/:id'
  ) {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('netvisor_accounting_exports_read')
    );
    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }
  if (path === '/synthetic-canary') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('synthetic_booking_canary_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/environment-variables') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('external_service_read')
    );
    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/environment-variables/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('external_service_update')
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/legal-compliance') {
    const hasReadPermission =
      userPermissions?.some((p) =>
        ['legal_compliance_read', 'cms_management_read'].some((key) =>
          p.permissions?.unique_key?.includes(key)
        )
      ) || isRouteAllowed(path, roles);

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  // Platform Setting permission routes
  if (path === '/platform-setting') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('platform_setting_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/platform-setting/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('platform_setting_update')
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  // KYC Management routes - gated on kyc_management_change_status, matching
  // the existing menu (useFilteredMenu.ts) and action-button (HarborDetailsView.tsx)
  // visibility checks, so a user who can't see the KYC Management entry
  // elsewhere can't reach these pages directly either (e.g. via the
  // post-login redirect to whatever page they were last on).
  if (path === '/kyc-management' || path === '/kyc-management/details') {
    const hasKycChangeStatusPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('kyc_management_change_status')
    );

    if (!hasKycChangeStatusPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/psp-fee-config') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('platform_setting_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  // Helpdesk FAQ permission routes
  if (path === '/helpdesk-faq/create') {
    const hasCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('faq_management_create')
    );

    if (!hasCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/helpdesk-faq/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('faq_management_update')
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/helpdesk-faq/view/:id' || path === '/helpdesk-faq') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('faq_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/penalty-config') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/penalty-config/create') {
    const hasCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_management_read')
    );

    if (!hasCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/penalty-config/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_management_read')
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/product-vat-config') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('product_vat_config_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/product-vat-config/create') {
    const hasCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes(
        'product_vat_config_management_create'
      )
    );

    if (!hasCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/product-vat-config/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes(
        'product_vat_config_management_update'
      )
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/commission') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('commission_policy_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/subscription-trial') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/guest-visibility') {
    const hasGuestVisibilityPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('guest_visibility_manage')
    );

    if (!hasGuestVisibilityPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/configuration/guest-booking-governance') {
    // Per-harbor overrides are managed on the harbor's own detail page now, so
    // this platform-wide page is hard-gated to platform admins — not merely
    // whoever happens to hold the permission key, since that key is otherwise
    // assignable to any role from Role Permissions.
    const hasGuestBookingGovernancePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('guest_booking_governance_manage')
    );

    if (!isPlatformAdminUser(roles) || !hasGuestBookingGovernancePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/configuration/entitlement-grants' ||
    path?.startsWith('/configuration/entitlement-grants/')
  ) {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('entitlement_grant_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/:harborId/commission') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('commission_policy_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-providers/:serviceProviderId/commission') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('commission_policy_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/cancellation-reports') {
    const hasCancellationReportsReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_reporting_view_reports')
    );

    if (!hasCancellationReportsReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/revenue-report') {
    const hasRevenueReportReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_reporting_view_reports')
    );

    if (!hasRevenueReportReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/historical-trends') {
    const hasHistoricalTrendsReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_reporting_view_reports')
    );

    if (!hasHistoricalTrendsReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/cms-management') {
    const hasCmsManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('cms_management_read')
    );

    if (!hasCmsManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/template-layout-management') {
    const hasTemplateLayoutManagementReadPermission = userPermissions?.some(
      (p) => p.permissions?.unique_key?.includes('cms_management_read')
    );

    if (!hasTemplateLayoutManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/testimonial-management' ||
    path === '/testimonial/create' ||
    path === '/testimonial/edit/:id' ||
    path === '/testimonial/view/:id'
  ) {
    const hasCmsManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('cms_management_read')
    );

    if (!hasCmsManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/article-management') {
    const hasArticleManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('article_management_read')
    );

    if (!hasArticleManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/article-management/create') {
    const hasArticleManagementCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('article_management_create')
    );

    if (!hasArticleManagementCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/article-management/edit/:id') {
    const hasArticleManagementUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('article_management_update')
    );

    if (!hasArticleManagementUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/article-management/view/:id') {
    const hasArticleManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('article_management_read')
    );

    if (!hasArticleManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/article-management/:id/versions') {
    const hasArticleManagementViewVersionsPermission = userPermissions?.some(
      (p) =>
        p.permissions?.unique_key?.includes('article_management_view_versions')
    );

    if (!hasArticleManagementViewVersionsPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/routing-management' ||
    path === '/routing-management/debugger' ||
    path === '/routing-management/details/:id' ||
    path === '/routing-management/version-details/:id' ||
    path === '/routing-management/version-sources/:id' ||
    path === '/routing-management/manual-route' ||
    path === '/routing-management/manual-route-details/:id'
  ) {
    const hasRoutingManagementReadPermission = userPermissions?.some(
      (p) => p.permissions?.unique_key === 'routing_management_read'
    );

    if (hasRoutingManagementReadPermission) {
      return children ? <>{children}</> : <Outlet />;
    }
  }

  // Theme Routes module: currently no dedicated permission keys.
  // Allow access for any authenticated user reaching these paths.
  if (path === '/theme-routes') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('theme_route_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/theme-routes/create') {
    const hasCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('theme_route_management_create')
    );

    if (!hasCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/theme-routes/edit/:id') {
    const hasUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('theme_route_management_update')
    );

    if (!hasUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/theme-routes/view/:id') {
    const hasReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('theme_route_management_read')
    );

    if (!hasReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/roles-management') {
    const hasRoleAndPermissionModule = userPermissions?.some(
      (p) => p.permissions?.module?.unique_key === 'role_and_permission'
    );

    if (hasRoleAndPermissionModule) {
      return children ? <>{children}</> : <Outlet />;
    }
  }

  if (path === '/organization-management') {
    const hasOrgManagementPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('organization_management')
    );

    if (hasOrgManagementPermission) {
      return children ? <>{children}</> : <Outlet />;
    }
  }

  if (path === '/subscription-management') {
    const hasSubscriptionManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('subscription_management_read')
    );

    if (!hasSubscriptionManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/details') {
    const hasHarborManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('harbor_management_read')
    );

    if (!hasHarborManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/deployments/:id') {
    const hasDeploymentManagementReadPermission = userPermissions?.some(
      (p) => p.permissions?.unique_key === 'deployment_management_read'
    );

    if (hasDeploymentManagementReadPermission) {
      return children ? <>{children}</> : <Outlet />;
    }
  }

  if (path === '/harbors/edit') {
    const hasHarborManagementUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('harbor_management_update')
    );

    if (!hasHarborManagementUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/create') {
    const hasHarborManagementCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('harbor_management_create')
    );

    if (!hasHarborManagementCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/bulk-import') {
    const hasHarborManagementCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('harbor_management_create')
    );

    if (!hasHarborManagementCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/bulk-update') {
    const hasHarborManagementBulkUpdatePermission = userPermissions?.some(
      (p) =>
        p.permissions?.unique_key?.includes('harbor_management_bulk_update')
    );

    if (!hasHarborManagementBulkUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/harbors/deletion-requests') {
    const hasHarborManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('harbor_management_read')
    );

    if (!hasHarborManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/community/details') {
    const hasCommunityManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('community_management_read')
    );

    if (!hasCommunityManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/community-management') {
    const hasCommunityManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('community_management_read')
    );

    if (!hasCommunityManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path.startsWith('/club-management')) {
    // Annual Inspection pages (under club management)
    if (
      path.startsWith('/club-management/') &&
      path.includes('/annual-inspections')
    ) {
      const isCreateAnnualInspection = path.endsWith(
        '/annual-inspections/create'
      );
      const isEditOrViewAnnualInspection =
        path.includes('/annual-inspections/') && !isCreateAnnualInspection;

      if (isCreateAnnualInspection) {
        const hasCreateInspectionPermission = userPermissions?.some((p) =>
          p.permissions?.unique_key?.includes(
            'club_resource_management_create_inspection_slot'
          )
        );

        if (!hasCreateInspectionPermission) {
          return (
            <Navigate to="/error/404" state={{ from: location }} replace />
          );
        }

        return children ? <>{children}</> : <Outlet />;
      }

      if (isEditOrViewAnnualInspection) {
        // We use query param for mode, but permission-wise allow view with read and edit with update.
        // Since this component only receives the route template, enforce update if route is the resource page.
        // The page itself controls whether it is editable.
        const hasReadPermission = userPermissions?.some((p) =>
          p.permissions?.unique_key?.includes('club_resource_management_read')
        );
        const hasUpdatePermission = userPermissions?.some((p) =>
          p.permissions?.unique_key?.includes('club_resource_management_update')
        );

        if (!hasReadPermission && !hasUpdatePermission) {
          return (
            <Navigate to="/error/404" state={{ from: location }} replace />
          );
        }

        return children ? <>{children}</> : <Outlet />;
      }
    }

    if (path === '/club-management') {
      const hasClubManagementReadPermission = userPermissions?.some((p) =>
        p.permissions?.unique_key?.includes('club_management_read')
      );

      if (!hasClubManagementReadPermission) {
        return <Navigate to="/error/404" state={{ from: location }} replace />;
      }
    } else if (path === '/club-management/create') {
      const hasClubManagementCreatePermission = userPermissions?.some((p) =>
        p.permissions?.unique_key?.includes('club_management_create')
      );

      if (!hasClubManagementCreatePermission) {
        return <Navigate to="/error/404" state={{ from: location }} replace />;
      }
    } else if (path.includes('/club-management/edit/')) {
      const hasClubManagementUpdatePermission = userPermissions?.some((p) =>
        p.permissions?.unique_key?.includes('club_management_update')
      );

      if (!hasClubManagementUpdatePermission) {
        return <Navigate to="/error/404" state={{ from: location }} replace />;
      }
    } else if (path.match(/\/club-management\/\w+/)) {
      const hasClubManagementReadPermission = userPermissions?.some((p) =>
        p.permissions?.unique_key?.includes('club_management_read')
      );

      if (!hasClubManagementReadPermission) {
        return <Navigate to="/error/404" state={{ from: location }} replace />;
      }
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/club-booking-management') {
    const hasClubBookingManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes(
        'club_resource_booking_management_read'
      )
    );

    if (!hasClubBookingManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path.startsWith('/club-resources/') && path.endsWith('/shifts')) {
    const hasClubResourceManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('club_resource_management_read')
    );

    if (!hasClubResourceManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/promo-management') {
    const hasPromoManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('coupon_management_read')
    );

    if (!hasPromoManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/promo-management/create') {
    const hasPromoManagementCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('coupon_management_create')
    );

    if (!hasPromoManagementCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/promo-management/:promoId/edit') {
    const hasPromoManagementUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('coupon_management_update')
    );

    if (!hasPromoManagementUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/booking-reminder-configuration') {
    const hasBookingReminderPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('booking_reminder_config')
    );

    if (!hasBookingReminderPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/discount-tracking-reports') {
    const hasDiscountReportsReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('coupon_management_read')
    );

    if (!hasDiscountReportsReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/transactions' ||
    path === '/transactions/:id' ||
    path === '/settlements' ||
    path === '/settlements/:id'
  ) {
    const hasPaymentManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('payment_management_read')
    );

    if (!hasPaymentManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (
    path === '/subscription-transactions' ||
    path === '/subscription-transactions/:id'
  ) {
    const hasInvoiceReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('invoice_management_read')
    );

    if (!hasInvoiceReadPermission && !isRouteAllowed(path, roles)) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/external-services') {
    const hasExternalServicesPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('third_party_integration_configure')
    );

    if (!hasExternalServicesPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-provider-management') {
    const hasServiceProviderManagementReadPermission = userPermissions?.some(
      (p) =>
        p.permissions?.unique_key?.includes('service_provider_management_read')
    );

    if (!hasServiceProviderManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/advertise-management') {
    const hasAdvertiseManagementReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('advertisement_management_read')
    );

    if (!hasAdvertiseManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/advertise/create') {
    const hasAdvertiseManagementCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('advertisement_management_create')
    );

    if (!hasAdvertiseManagementCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/advertise/edit/:id') {
    const hasAdvertiseManagementEditPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('advertisement_management_update')
    );

    if (!hasAdvertiseManagementEditPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-providers/details') {
    const hasServiceProviderDetailsPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_provider_management_read')
    );

    if (!hasServiceProviderDetailsPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-providers/requests') {
    const hasRequestsPermission = userPermissions?.some(
      (p) => p.permissions?.unique_key === 'service_provider_management_read'
    );

    if (!hasRequestsPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-providers/create') {
    const hasServiceProviderCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_provider_management_create')
    );

    if (!hasServiceProviderCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }
  if (path === '/notification-preferences') {
    const hasNotificationPreferencesReadPermission = userPermissions?.some(
      (p) => p.permissions?.unique_key?.includes('notification_rule_read')
    );

    if (!hasNotificationPreferencesReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-providers/edit/:id') {
    const hasServiceProviderUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_provider_management_update')
    );

    if (!hasServiceProviderUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-management') {
    const hasServiceManagementReadPermission = userPermissions?.some(
      (p) =>
        p.permissions?.unique_key?.includes('service_management_read') ||
        p.permissions?.unique_key?.includes('service_provider_management_read')
    );

    if (!hasServiceManagementReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-moderation') {
    const hasServiceModerationApprovePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_moderation_approve')
    );

    const hasServiceModerationRejectPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_moderation_reject')
    );

    if (
      !hasServiceModerationApprovePermission ||
      !hasServiceModerationRejectPermission
    ) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/service-moderation/details/:id') {
    const hasServiceModerationApprovePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_moderation_approve')
    );

    const hasServiceModerationRejectPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('service_moderation_reject')
    );

    if (
      !hasServiceModerationApprovePermission ||
      !hasServiceModerationRejectPermission
    ) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  // System Announcements: allow only ADMIN users, independent of API route mapping
  if (path === '/system-announcements') {
    const hasServiceProviderUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('system_announcements_read')
    );

    if (!hasServiceProviderUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management') {
    const hasDmoReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_registration_management_read')
    );

    if (!hasDmoReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management/register/:id') {
    const hasDmoUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_registration_management_update')
    );

    if (!hasDmoUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management/register') {
    const hasDmoCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_registration_management_create')
    );

    if (!hasDmoCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management/details/:id') {
    const hasDmoReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_registration_management_read')
    );

    if (!hasDmoReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management/staffs') {
    const hasDmoStaffReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_staff_management_read')
    );

    if (!hasDmoStaffReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/list-value-management') {
    const hasListValueReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('list_value_management_read')
    );

    if (!hasListValueReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/list-value-management/create/:type') {
    const hasListValueCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('list_value_management_create')
    );

    if (!hasListValueCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/list-value-management/edit/:type/:id') {
    const hasListValueUpdatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('list_value_management_update')
    );

    if (!hasListValueUpdatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/dmo-management/staffs/create') {
    const hasDmoStaffCreatePermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('dmo_staff_management_create')
    );

    if (!hasDmoStaffCreatePermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (path === '/region-management') {
    const hasRegionReadPermission = userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('region_management_read')
    );

    if (!hasRegionReadPermission) {
      return <Navigate to="/error/404" state={{ from: location }} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
  }

  if (!isRouteAllowed(path, roles)) {
    return <Navigate to="/error/404" state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}
